import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import JobCard from "../components/JobCard";
import AvatarUpload from "../components/AvatarUpload";
import EventCard from "../components/EventCard";
import { FREELANCE_SPECIALIZATIONS } from "../data/specializations";
import { toast } from "sonner";

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "overview");
  const [apps, setApps] = useState([]);
  const [posted, setPosted] = useState([]);
  const [receivedApps, setReceivedApps] = useState([]);

  useEffect(() => {
    api.get("/me/applications").then(r => setApps(r.data)).catch(() => {});
    api.get("/me/jobs").then(r => setPosted(r.data)).catch(() => {});
    if (user?.role === "manager" || user?.role === "freelancer") {
      api.get("/me/received-applications").then(r => setReceivedApps(r.data)).catch(() => {});
    }
  }, [user?.role]);

  if (!user) return null;

  // "profile" tab renamed to "settings" — content is unchanged, just the label
  const tabs = user.role === "worker"
    ? ["overview", "applied", "confirmed", "waitlist", "history", "settings"]
    : user.role === "manager"
      ? ["overview", "events", "my-jobs", "settings"]
      : user.role === "freelancer"
        ? ["overview", "freelance-profile", "my-jobs", "settings"]
        : ["overview", "settings"];

  const tabLabel = (t) => {
    if (t === "settings") return "Settings";
    if (t === "my-jobs") return "My Jobs";
    if (t === "freelance-profile") return "Freelance Profile";
    return t.charAt(0).toUpperCase() + t.slice(1);
  };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8" data-testid="dashboard">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <span className="label-eyebrow text-muted-foreground">Dashboard · {user.role}</span>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl tracking-tighter text-foreground">Hi, {user.name?.split(" ")[0]}</h1>
        </div>
        {user.role !== "worker" && <Link to="/post-job" className="btn-primary">+ Post a Job</Link>}
      </div>


      <nav className="mt-8 border-b border-border flex gap-2 overflow-x-auto" data-testid="dashboard-tabs">
        {tabs.map(t => (
          <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
            className={`px-4 py-2 text-sm whitespace-nowrap transition ${tab===t?"border-b-2 border-brand text-brand font-semibold":"text-muted-foreground hover:text-brand"}`}>
            {tabLabel(t)}
          </button>
        ))}
      </nav>

      <div className="mt-8">
        {tab === "overview"          && <OverviewTab user={user} apps={apps} posted={posted} receivedApps={receivedApps} />}
        {tab === "applied"           && <AppliedJobsGrid apps={apps} empty="No applications yet." />}
        {tab === "confirmed"         && <JobsGrid jobs={apps.filter(a => a.status==="confirmed").map(a => a.job).filter(Boolean)} empty="Nothing confirmed." />}
        {tab === "waitlist"          && <JobsGrid jobs={apps.filter(a => a.status==="waitlist").map(a => a.job).filter(Boolean)} empty="Not on any waitlist." />}
        {tab === "history"           && <JobsGrid jobs={apps.map(a => a.job).filter(Boolean)} empty="No history yet." />}
        {tab === "events"            && <EventsTab />}
        {tab === "my-jobs"           && <MyJobs posted={posted} />}
        {tab === "freelance-profile" && <FreelancerProfileEditor />}
        {tab === "settings"          && <ProfileEditor user={user} onUpdate={refresh} />}
      </div>
    </div>
  );
}

// ---------- Overview Tab ----------

function OverviewTab({ user, apps, posted, receivedApps }) {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    api.get("/events").then(r => setEvents(r.data)).catch(() => {});
  }, [user.role]);

  const ownedEvents = events.filter(e => e.posted_by === user.id);
  const coManagedEvents = events.filter(e => e.posted_by !== user.id);
  const visibleEvents = ownedEvents.filter(e => e.status !== "cancelled");
  const activeEvents = visibleEvents.filter(e => e.status !== "completed");
  const completedEvents = visibleEvents.filter(e => e.status === "completed");
  const confirmedReceived = receivedApps.filter(a => a.status === "confirmed");
  const waitlistReceived = receivedApps.filter(a => a.status === "waitlist");

  return (
    <div className="space-y-6">

      {/* Profile card */}
      <div className="card-flat flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <AvatarUpload size="lg" />
          <div>
            <p className="font-display text-xl text-foreground">{user.name}</p>
            <p className="text-sm text-muted-foreground">@{user.username}</p>
            {user.points != null && (
              <p className="text-xs text-amber-500 mt-1">⭐ {user.points} points</p>
            )}
          </div>
        </div>
        <Link to={`/profile/${user.username}`} className="btn-tint text-sm">
          View my profile →
        </Link>
      </div>

      {/* ── CO-MANAGED EVENTS (shown for any role, only if invited & accepted) ── */}
      {coManagedEvents.length > 0 && (
        <div>
          <h2 className="font-display text-xl mb-0.5">Events you co-manage</h2>
          <p className="text-xs text-muted-foreground mb-3">Events where you've been added as a co-manager</p>
          <div className="space-y-4">
            {coManagedEvents.map(e => <EventCard key={e.id} event={e} />)}
          </div>
        </div>
      )}

      {/* ── MANAGER ── */}
      {user.role === "manager" && (
        <div className="space-y-8">
          <div>
            <div className="flex items-center justify-between mb-1">
              <div>
                <h2 className="font-display text-xl">Your Events</h2>
                <p className="text-xs text-muted-foreground mt-0.5">Events you have created</p>
              </div>
              <a href="/create-event" className="btn-primary text-sm !py-2">+ Create Event</a>
            </div>
            {visibleEvents.length === 0 ? (
              <div className="card-flat text-center py-8 mt-3">
                <p className="text-muted-foreground text-sm">No events yet. Create your first event to get started!</p>
                <a href="/create-event" className="btn-primary mt-3 inline-flex text-sm">+ Create Event</a>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                <Stat k="Total events" v={visibleEvents.length} />
                <Stat k="Active" v={activeEvents.length} hint="not yet completed" />
                <Stat k="Completed" v={completedEvents.length} />
                <Stat k="Total roles" v={visibleEvents.reduce((s, e) => s + (e.job_count || 0), 0)} hint="job roles across all your events" />
              </div>
            )}
          </div>

          <div>
            <h2 className="font-display text-xl mb-0.5">Your Job Posts</h2>
            <p className="text-xs text-muted-foreground mb-3">Standalone jobs you posted — showing applications received from workers</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat k="Posted jobs" v={posted.filter(p => p.status !== "cancelled").length} />
              <Stat k="Applications received" v={receivedApps.length} hint="workers who applied to your jobs" />
              <Stat k="Confirmed workers" v={confirmedReceived.length} hint="applications you accepted" />
              <Stat k="On waitlist" v={waitlistReceived.length} />
            </div>
          </div>
        </div>
      )}

      {/* ── WORKER ── */}
      {user.role === "worker" && (
        <div>
          <h2 className="font-display text-xl mb-0.5">Your Activity</h2>
          <p className="text-xs text-muted-foreground mb-3">Jobs you have applied to</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat k="Applications sent" v={apps.length} />
            <Stat k="Confirmed" v={apps.filter(a => a.status === "confirmed").length} />
            <Stat k="Waitlist" v={apps.filter(a => a.status === "waitlist").length} />
            <Stat k="Jobs attended" v={apps.filter(a => a.attendance === "present").length} hint="marked present by manager" />
          </div>
        </div>
      )}

      {/* ── FREELANCER ── */}
      {user.role === "freelancer" && (
        <div className="space-y-8">
          <div>
            <h2 className="font-display text-xl mb-0.5">Your Job Posts</h2>
            <p className="text-xs text-muted-foreground mb-3">Freelance jobs you posted — showing applications received</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat k="Posted jobs" v={posted.length} />
              <Stat k="Applications received" v={receivedApps.length} hint="people who applied to your posts" />
              <Stat k="Confirmed" v={confirmedReceived.length} />
              <Stat k="On waitlist" v={waitlistReceived.length} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ k, v, hint }) {
  return (
    <div className="border border-border p-4 rounded-sm bg-background">
      <p className="font-display text-3xl text-foreground">{v}</p>
      <p className="text-xs text-muted-foreground mt-1">{k}</p>
      {hint && <p className="text-xs text-muted-foreground/50 mt-0.5">{hint}</p>}
    </div>
  );
}

// ---------- Settings (was Profile) ----------

function ProfileEditor({ user, onUpdate }) {
  const [form, setForm] = useState({
    name: user.name || "",
    username: user.username || "",
    bio: user.bio || "",
    phone: user.phone || "",
    company_name: user.company_name || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/users/me", form);
      await onUpdate();
      toast.success("Settings saved!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-md">
      <div className="flex items-center gap-4 mb-6">
        <AvatarUpload size="lg" />
        <div>
          <p className="font-medium text-foreground">{user.name}</p>
          <p className="text-sm text-muted-foreground">@{user.username}</p>
          <p className="text-xs text-brand mt-1">Click camera icon to change photo</p>
        </div>
      </div>

      <form onSubmit={save} className="space-y-4">
        <div>
          <label className="label-eyebrow text-muted-foreground">Full name</label>
          <input className="input-base mt-2" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required />
        </div>
        <div>
          <label className="label-eyebrow text-muted-foreground">Username</label>
          <div className="relative mt-2">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">@</span>
            <input className="input-base pl-7" value={form.username} onChange={e => setForm({...form, username: e.target.value.toLowerCase().replace(/[^a-z0-9._]/g, "")})} required />
          </div>
          <p className="text-xs text-muted-foreground mt-1">Lowercase letters, numbers, . and _ only</p>
        </div>
        <div>
          <label className="label-eyebrow text-muted-foreground">Bio</label>
          <textarea rows={3} className="input-base mt-2" placeholder="Tell people about yourself…" value={form.bio} onChange={e => setForm({...form, bio: e.target.value})} />
        </div>
        <div>
          <label className="label-eyebrow text-muted-foreground">Phone</label>
          <input className="input-base mt-2" placeholder="+91 XXXX XXXXXX" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
          <p className="text-xs text-muted-foreground mt-1">Used by managers to contact you. Not shown publicly.</p>
        </div>
        {user.role === "manager" && (
          <div>
            <label className="label-eyebrow text-muted-foreground">Organization / company name</label>
            <input className="input-base mt-2" placeholder="e.g. Malabar Events Co." value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} data-testid="settings-company-name" />
            <p className="text-xs text-muted-foreground mt-1">Shown to workers when you post jobs.</p>
          </div>
        )}
        <button disabled={saving} className="btn-primary w-full">
          {saving ? "Saving…" : "Save changes"}
        </button>
      </form>
    </div>
  );
}

// ---------- Events Tab ----------

function EventsTab() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/events").then(r => { setEvents(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const liveEvents = events.filter(e => e.status !== "completed" && e.status !== "cancelled");
  const completedEvents = events.filter(e => e.status === "completed");
  const cancelledEvents = events.filter(e => e.status === "cancelled");

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">{events.length} event{events.length !== 1 ? "s" : ""}</p>
        <a href="/create-event" className="btn-primary text-sm !py-2">+ Create Event</a>
      </div>
      {events.length === 0
        ? <div className="card-flat text-center py-12">
            <p className="font-display text-xl">No events yet</p>
            <p className="text-sm text-muted-foreground mt-1">Create your first event to manage multiple job roles together</p>
            <a href="/create-event" className="btn-primary mt-4 inline-flex">+ Create Event</a>
          </div>
        : (
          <div className="space-y-8">
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                Live events ({liveEvents.length})
              </h3>
              {liveEvents.length === 0
                ? <p className="text-sm text-muted-foreground">No live events right now.</p>
                : <div className="space-y-4">{liveEvents.map(e => <EventCard key={e.id} event={e} />)}</div>
              }
            </div>
            {completedEvents.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                  Completed events ({completedEvents.length})
                </h3>
                <div className="space-y-4">{completedEvents.map(e => <EventCard key={e.id} event={e} />)}</div>
              </div>
            )}
            {cancelledEvents.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                  Cancelled events ({cancelledEvents.length})
                </h3>
                <div className="space-y-4">{cancelledEvents.map(e => <EventCard key={e.id} event={e} />)}</div>
              </div>
            )}
          </div>
        )
      }
    </div>
  );
}

function JobsGrid({ jobs, empty }) {
  if (!jobs.length) return <p className="text-sm text-muted-foreground">{empty}</p>;
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{jobs.map(j => j && <JobCard key={j.id} job={j} />)}</div>;
}
function AppliedJobsGrid({ apps, empty }) {
  const valid = apps.filter(a => a.job);
  if (!valid.length) return <p className="text-sm text-muted-foreground">{empty}</p>;
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{valid.map(a => <JobCard key={a.job.id} job={a.job} application={a} />)}</div>;
}

function MyJobs({ posted }) {
  if (posted.length === 0) {
    return <p className="text-sm text-muted-foreground">You haven't posted any jobs yet.</p>;
  }

  const liveJobs = posted.filter(j => j.status !== "completed" && j.status !== "cancelled");
  const completedJobs = posted.filter(j => j.status === "completed");
  const cancelledJobs = posted.filter(j => j.status === "cancelled");

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
          Live jobs ({liveJobs.length})
        </h3>
        {liveJobs.length === 0
          ? <p className="text-sm text-muted-foreground">No live jobs right now.</p>
          : <div className="space-y-3">{liveJobs.map(j => <JobRow key={j.id} j={j} />)}</div>
        }
      </div>
      {completedJobs.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            Completed jobs ({completedJobs.length})
          </h3>
          <div className="space-y-3">{completedJobs.map(j => <JobRow key={j.id} j={j} />)}</div>
        </div>
      )}
      {cancelledJobs.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            Cancelled jobs ({cancelledJobs.length})
          </h3>
          <div className="space-y-3">{cancelledJobs.map(j => <JobRow key={j.id} j={j} />)}</div>
        </div>
      )}
    </div>
  );
}

function JobRow({ j }) {
  return (
    <Link to={`/job/${j.id}`} className="card-flat block">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="font-display text-lg text-foreground">{j.title}</p>
          <p className="text-xs text-muted-foreground font-mono">{j.specialization} · {j.opportunity_type}</p>
        </div>
        <span className="text-xs px-2 py-1 bg-muted rounded-sm text-muted-foreground">View →</span>
      </div>
    </Link>
  );
}

function FreelancerProfileEditor() {
  const [form, setForm] = useState({
    about: "", skills: "", specializations: [], pricing: "", experience_level: "beginner",
    availability: true, portfolio_links: { instagram: "", behance: "", linkedin: "", website: "", drive: "" },
    portfolio_images: "",
  });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then(({ data: u }) => {
      api.get(`/freelancers/${u.username}`).then(({ data: p }) => {
        setForm({
          about: p.about || "",
          skills: (p.skills || []).join(", "),
          specializations: p.specializations || [],
          pricing: p.pricing || "",
          experience_level: p.experience_level || "beginner",
          availability: p.availability !== false,
          portfolio_links: p.portfolio_links || { instagram:"", behance:"", linkedin:"", website:"", drive:"" },
          portfolio_images: (p.portfolio_images || []).join("\n"),
        });
        setLoaded(true);
      }).catch(() => setLoaded(true));
    });
  }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/freelancer/profile", {
        about: form.about,
        skills: form.skills.split(",").map(s=>s.trim()).filter(Boolean),
        specializations: form.specializations,
        pricing: form.pricing,
        experience_level: form.experience_level,
        availability: form.availability,
        portfolio_links: form.portfolio_links,
        portfolio_images: form.portfolio_images.split("\n").map(s=>s.trim()).filter(Boolean),
      });
      toast.success("Profile saved");
    } catch { toast.error("Failed to save"); }
  };

  if (!loaded) return null;
  const toggleSpec = (s) => setForm(f => ({ ...f, specializations: f.specializations.includes(s) ? f.specializations.filter(x=>x!==s) : [...f.specializations, s] }));

  return (
    <form onSubmit={save} className="max-w-2xl space-y-4" data-testid="freelancer-profile-form">
      <div><label className="label-eyebrow text-muted-foreground">About me</label><textarea rows={4} className="input-base mt-2" value={form.about} onChange={e=>setForm({...form,about:e.target.value})} /></div>
      <div><label className="label-eyebrow text-muted-foreground">Skills (comma-separated)</label><input className="input-base mt-2" value={form.skills} onChange={e=>setForm({...form,skills:e.target.value})} /></div>
      <div>
        <label className="label-eyebrow text-muted-foreground">Specializations</label>
        <div className="mt-2 flex flex-wrap gap-2">
          {FREELANCE_SPECIALIZATIONS.map(s => (
            <button type="button" key={s} onClick={() => toggleSpec(s)} className={`px-3 py-1.5 rounded-sm text-xs border transition ${form.specializations.includes(s)?"bg-brand text-white border-brand":"border-border hover:border-brand hover:bg-brand-tint text-foreground"}`}>{s}</button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="label-eyebrow text-muted-foreground">Pricing</label><input className="input-base mt-2" placeholder="₹500/hr" value={form.pricing} onChange={e=>setForm({...form,pricing:e.target.value})} /></div>
        <div><label className="label-eyebrow text-muted-foreground">Experience</label>
          <select className="input-base mt-2" value={form.experience_level} onChange={e=>setForm({...form,experience_level:e.target.value})}>
            <option value="beginner">Beginner</option><option value="intermediate">Intermediate</option><option value="expert">Expert</option>
          </select>
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm text-foreground"><input type="checkbox" checked={form.availability} onChange={e=>setForm({...form,availability:e.target.checked})} /> Available for hire</label>
      <div>
        <label className="label-eyebrow text-muted-foreground">Portfolio links</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
          {["instagram","behance","linkedin","website","drive"].map(k => (
            <input key={k} className="input-base" placeholder={k} value={form.portfolio_links[k]} onChange={e=>setForm({...form, portfolio_links:{...form.portfolio_links,[k]:e.target.value}})} />
          ))}
        </div>
      </div>
      <div><label className="label-eyebrow text-muted-foreground">Portfolio image URLs (one per line)</label><textarea rows={3} className="input-base mt-2" value={form.portfolio_images} onChange={e=>setForm({...form,portfolio_images:e.target.value})} /></div>
      <button className="btn-primary">Save profile</button>
    </form>
  );
}
